import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, TextField, Divider, CircularProgress } from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import FileDownloadDoneOutlinedIcon from '@mui/icons-material/FileDownloadDoneOutlined';
import SettingsBackupRestoreOutlinedIcon from '@mui/icons-material/SettingsBackupRestoreOutlined';
import UIRTMButton from "../UIRTMButton";
import SleekSingleSelect from "../SleekSingleSelect";
import { toast } from "react-toastify";
import axios from "axios";
import { checkEmail } from "../../checkFields";
import { ALM_SETTINGS } from "../../../config/rtmSettingsSchema";
import "./RTMSettings.css";

import LanguageIcon from "@mui/icons-material/Language";
import EmailIcon from "@mui/icons-material/Email";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

const ALMIntegrationFeature = ({ projectId, siteId, config, onUpdate }) => {
  const [activeKey, setActiveKey] = useState("");
  const [almConfig, setAlmConfig] = useState({});
  const [initialState, setInitialState] = useState({
    activeALM: "",
    almProviders: {},
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [errors, setErrors] = useState({});

  const getFieldIcon = (key) => {
    switch (key) {
      case "domain":
        return <LanguageIcon sx={{ color: "#3a85ef", fontSize: "1.1rem" }} />;
      case "email":
        return <EmailIcon sx={{ color: "#3a85ef", fontSize: "1.1rem" }} />;
      case "token":
        return <VpnKeyIcon sx={{ color: "#3a85ef", fontSize: "1.1rem" }} />;
      default:
        return <VpnKeyIcon sx={{ color: "#3a85ef", fontSize: "1.1rem" }} />;
    }
  };

  useEffect(() => {
    if (config) {
      const active = config.activeALM || "";
      const providers = config.almProviders || {};
      setActiveKey(active);
      setAlmConfig(providers[active] || {});
      setInitialState({
        activeALM: active,
        almProviders: JSON.parse(JSON.stringify(providers)),
      });
    }
  }, [config]);

  const handleFieldChange = (field, value) => {
    setAlmConfig((prev) => ({
      ...prev,
      [field]: value,
    }));

    // OPTIONAL: clear error while typing (better UX)
    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const handleTypeChange = (newTypeKey) => {
    setActiveKey(newTypeKey);
    setAlmConfig(initialState.almProviders[newTypeKey] || {});
  };

  const hasChanges = () => {
    if (activeKey !== initialState.activeALM) return true;
    const currentFields = almConfig;
    const initialFields = initialState.almProviders[activeKey] || {};

    const fields = ALM_SETTINGS[activeKey]?.fields || [];
    return fields.some(
      (f) => (currentFields[f.key] || "") !== (initialFields[f.key] || ""),
    );
  };

  const validateFields = (data = almConfig) => {
    const newErrors = {};
    const fields = ALM_SETTINGS[activeKey]?.fields || [];

    let firstErrorField = null;

    fields.forEach((f) => {
      const val = data[f.key];

      if (!val || val.trim() === "") {
        newErrors[f.key] = `${f.label} is required`;
        if (!firstErrorField) firstErrorField = f.key;
        return;
      }

      if (f.type === "email" && !checkEmail(val)) {
        newErrors[f.key] = "Enter a valid email address";
        if (!firstErrorField) firstErrorField = f.key;
      }

      if (f.type === "url" && !isValidUrl(val)) {
        newErrors[f.key] = "Enter a valid URL (https://...)";
        if (!firstErrorField) firstErrorField = f.key;
      }
    });

    setErrors(newErrors);
    if (firstErrorField) {
      const el = document.querySelector(`[name="${firstErrorField}"]`);
      if (el) el.focus();
    }

    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (value) => {
    try {
      const url = new URL(value);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  };

  const resetData = () => {
    setActiveKey(initialState.activeALM);
    setAlmConfig(initialState.almProviders[initialState.activeALM] || {});
    toast.info("Changes reverted");
  };

  const handleSave = async () => {
     if (!validateFields()) {
      toast.error("Please fix the highlighted errors");
      return;
    } 

    setIsLoading(true);
    const userDetails = JSON.parse(sessionStorage.getItem("UserDetails"));
    const headerData = {
      Authorization: sessionStorage.getItem("authToken") || "",
      "ch-subscription-key":
        userDetails?.data?.User?.["ch-subscription-key"] || "",
    };

    const url = `${window.configs.BASE_API_URL}${window.configs.ADMIN_SERVICE_PROJECT_URL}/${projectId}/${siteId}/rtmSettings`;

    const updatedProviders = {
      ...initialState.almProviders,
      [activeKey]: almConfig,
    };
    const payload = {
      rtm_settings: {
        activeALM: activeKey,
        almProviders: updatedProviders,
      },
    };

    try {
      const result = await axios.patch(url, payload, { headers: headerData });
      if (result.data.status === "success") {
        toast.success("ALM Configuration saved successfully");
        if (onUpdate) onUpdate();
      } else {
        toast.error(result.data.message || "Failed to save configuration");
      }
    } catch (error) {
      toast.error("Error saving ALM configuration");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTest = async () => {
    if (!validateFields()) {
      toast.error("Please fix the highlighted errors");
      return;
    }
    setIsTesting(true);
    try {
      const apiUrl = `${window.configs.BASE_API_URL}${window.configs.AIQA_JIRA_HANDLER_URL}`;
      const payload = {
        action: "test_connection",
        credentials: { ...almConfig },
      };
      const response = await axios.post(apiUrl, payload);
      if (response?.data?.status === "success") {
        toast.success(
          `${ALM_SETTINGS[activeKey].label} Connection successful!`,
        );
      } else {
        toast.error("Connection failed");
      }
    } catch (error) {
      toast.error(`Failed to connect to ${ALM_SETTINGS[activeKey]?.label}`);
    } finally {
      setIsTesting(false);
    }
  };
  const validateSingleField = (fieldKey, value) => {
    const field = ALM_SETTINGS[activeKey]?.fields.find(
      (f) => f.key === fieldKey,
    );
    if (!field) return;

    let error = "";

    if (!value || value.trim() === "") {
      error = `${field.label} is required`;
    } else if (field.type === "email" && !checkEmail(value)) {
      error = "Enter a valid email address";
    } else if (field.type === "url" && !isValidUrl(value)) {
      error = "Enter a valid URL (https://...)";
    }

    setErrors((prev) => ({
      ...prev,
      [fieldKey]: error,
    }));
  };

  const providerOptions = Object.keys(ALM_SETTINGS).map((key) => ({
    id: key,
    name: ALM_SETTINGS[key].label,
  }));
  const isValid = Object.keys(errors).length === 0;
  return (
    <Box className="alm-feature-container">
      <p className="alert-info-label" style={{ textAlign: "center" }}>
        {ALM_SETTINGS.description ||
          "Connect Application Lifecycle Management tool to enable requirement traceability"}
      </p>
      <Divider sx={{ my: 2 }} />

      <Box className="alm-fields-stack">
        <Box className="alm-field-row">
          <label className="alm-dropdown-label">
            <AdminPanelSettingsIcon sx={{ color: "#3a85ef", fontSize: "1.1rem", mr: 1, verticalAlign: "middle" }} />
            Select ALM Tool
          </label>
          <SleekSingleSelect
            placeholder="Select Tool"
            options={providerOptions}
            selectedValue={activeKey} // this is "jira" or "ado"
            onChange={(id) => handleTypeChange(id)}
            isOpen={isDropdownOpen}
            setOpen={setDropdownOpen}
            width="300px"
          />
        </Box>

        {activeKey && ALM_SETTINGS[activeKey] && (
          <Box className="alm-fields-stack">
            {ALM_SETTINGS[activeKey].fields.map((field) => (
              <Box className="alm-field-row" key={field.key}>
                <label className="alm-field-label">
                  {getFieldIcon(field.key)}
                  <span style={{ marginLeft: "8px" }}>{field.label}</span> 
                  <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                </label>
                <TextField
                  name={field.key}
                  fullWidth
                  size="small"
                  variant="outlined"
                  type={field.type || "text"}
                  placeholder={field.placeholder}
                  value={almConfig[field.key] || ""}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  onBlur={(e) => validateSingleField(field.key, e.target.value)}
                  error={!!errors[field.key]}
                  helperText={errors[field.key]}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#f9fafb",
                      "& fieldset": { borderColor: "#e5e7eb" },
                      "&:hover fieldset": { borderColor: "#3a85ef" },
                      "&.Mui-focused fieldset": { borderColor: "#3a85ef" },
                    }
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {activeKey && (
        <Box className="alm-buttons-container">
          <UIRTMButton
            variant="text"
            title="Cancel"
            disabled={!hasChanges() || isLoading || isTesting}
            onClick={resetData}
            icon={<SettingsBackupRestoreOutlinedIcon sx={{fontSize:"0.9rem"}}/>}
          />
          <UIRTMButton
            variant="text"
            title={isTesting ? "Testing..." : "Test Connection"}
            loading={isTesting}
            disabled={isLoading || isTesting}
            onClick={handleTest}
            icon={<FileDownloadDoneOutlinedIcon sx={{fontSize:"0.9rem"}}/>}
          />
          <UIRTMButton
            variant="text"
            title={isLoading ? "Saving..." : "Save"}
            loading={isLoading}
            disabled={!hasChanges() || isLoading || isTesting}
            onClick={handleSave}
            icon={<SaveOutlinedIcon sx={{fontSize:"0.9rem"}}/>}
          />
        </Box>
      )}
    </Box>
  );
};

ALMIntegrationFeature.propTypes = {
  projectId: PropTypes.string.isRequired,
  siteId: PropTypes.string.isRequired,
  config: PropTypes.object.isRequired,
  onUpdate: PropTypes.func,
};

export default ALMIntegrationFeature;
